import React, { useEffect, useState } from "react";
import serviceApi from "../../../services/serviceApi";
import environment from "../../../environments/environment";

type GalleryItem = {
    id: string;
    title?: string;
    dateTime?: string;
    url?: string;
    decription?: string;
    album?: string;
};

type Album = {
    id: string;
    title?: string;
    dateTime?: string;
    url?: string;
    decription?: string;
    items?: GalleryItem[];
};

const layoutOptions = [
    { value: 1, count: 1, columns: 1 },
    { value: 4, count: 4, columns: 2 },
    { value: 6, count: 9, columns: 3 },
    { value: 8, count: 8, columns: 4 }
];

const GalleryContent: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [albums, setAlbums] = useState<Album[]>([]);
    const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
    const [layout, setLayout] = useState(6);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    useEffect(() => {
        Gallery();
    }, []);

    const formatImage = (url?: string) => {
        if (!url) return "";
        if (url.startsWith("http")) return url;
        return `https://storage.googleapis.com/${url}`;
    };

    const Gallery = async () => {
        setIsLoading(true);
        try {
            const response = await serviceApi.get(environment.gallery);
            if (response?.status === "SUCCESS") {
                const formatted = (response?.dataList || []).map((album: Album) => ({
                    ...album,
                    url: formatImage(album.url)
                }));
                setAlbums(formatted);
            }
        } catch (error) {
            console.error("Gallery fetch failed", error);
        } finally {
            setIsLoading(false);
        }
    };

    const AlbumDetails = async (albumId: string) => {
        setIsLoading(true);
        try {
            const response = await serviceApi.get(
                `${environment.gallery}?id=${albumId}`
            );
            if (response?.status === "SUCCESS" && response?.objectData) {
                const album = response.objectData;
                const formattedAlbum = {
                    ...album,
                    url: formatImage(album.url),
                    items: (album.items || []).map((item: GalleryItem) => ({
                        ...item,
                        url: formatImage(item.url)
                    }))
                };
                setSelectedAlbum(formattedAlbum);
            }
        } catch (error) {
            console.error("Album fetch failed", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getGridColumns = () => {
        const selected = layoutOptions.find((x) => x.value === layout);
        return selected?.columns || 3;
    };

    const openImage = (url?: string) => {
        if (!url) return;
        setSelectedImage(url);
        document.body.style.overflow = "hidden";
    };

    const closeImage = () => {
        setSelectedImage(null);
        document.body.style.overflow = "auto";
    };

    return (

        <div className="notification-page">
            <div style={{ padding: "12px 12px 0 12px" }}>
                <h2 className="title-text notification-heading" style={{ margin: 0 }}>
                    Gallery
                </h2>
            </div>
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-overlay__content">
                        <div className="spinner spinner--lg"></div>
                        <span className="loading-text">Loading gallery...</span>
                    </div>
                </div>
            )}
            <div
                style={{
                    display: "flex",
                    gap: "10px",
                    padding: "10px",
                    borderBottom: "1px solid #eee"
                }}
            >
                {layoutOptions.map((option) => (
                    <div
                        key={option.value}
                        onClick={() => setLayout(option.value)}
                        style={{
                            width: "34px",
                            height: "34px",
                            padding: "4px",
                            borderRadius: "6px",
                            border:
                                layout === option.value
                                    ? "2px solid var(--colorPrimaryDark)"
                                    : "1px solid #aaa",
                            cursor: "pointer",
                            display: "grid",
                            gridTemplateColumns: `repeat(${option.columns}, 1fr)`,
                            gap: "2px"
                        }}
                    >
                        {Array.from({ length: option.count }).map((_, index) => (
                            <span
                                key={index}
                                style={{
                                    border: "1px solid #777",
                                    borderRadius: "2px",
                                    minHeight: "4px"
                                }}
                            />
                        ))}
                    </div>
                ))}
            </div>
            {!selectedAlbum && (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)`,
                        gap: "10px",
                        padding: "10px"
                    }}
                >
                    {albums.map((album) => (
                        <div
                            key={album.id}
                            onClick={() => AlbumDetails(album.id)}
                            style={{
                                background: "#fff",
                                borderRadius: "10px",
                                overflow: "hidden",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                                cursor: "pointer"
                            }}
                        >
                            {album.url && (
                                <img
                                    src={album.url}
                                    alt={album.title}
                                    style={{
                                        width: "100%",
                                        height: "180px",
                                        objectFit: "cover"
                                    }}
                                />
                            )}
                            <div
                                style={{
                                    padding: "8px",
                                    textAlign: "center",
                                    fontWeight: "600",
                                    color: "var(--colorPrimaryDark)"
                                }}
                            >
                                {album.title || "Untitled Album"}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {selectedAlbum && (
                <>
                    <div
                        style={{
                            padding: "12px",
                            background: "#fff",
                            margin: "10px",
                            borderRadius: "10px"
                        }}
                    >
                        <button
                            onClick={() => setSelectedAlbum(null)}
                            style={{
                                marginBottom: "10px",
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                fontWeight: "bold",
                                color: "var(--colorPrimaryDark)"
                            }}
                        >
                            ← Back
                        </button>
                        <h3>{selectedAlbum.title}</h3>
                        {selectedAlbum.decription && (
                            <p>{selectedAlbum.decription}</p>
                        )}
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)`,
                            gap: "10px",
                            padding: "10px"
                        }}
                    >
                        {selectedAlbum.items?.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => openImage(item.url)}
                                style={{
                                    background: "#fff",
                                    borderRadius: "10px",
                                    overflow: "hidden",
                                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                                    cursor: "pointer"
                                }}
                            >
                                {item.url ? (
                                    <img
                                        src={item.url}
                                        alt={item.title}
                                        style={{
                                            width: "100%",
                                            height: "180px",
                                            objectFit: "cover"
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            height: "180px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}
                                    >
                                        No Image
                                    </div>
                                )}
                                {item.title && (
                                    <div
                                        style={{
                                            padding: "8px",
                                            textAlign: "center",
                                            fontWeight: "600"
                                        }}
                                    >
                                        {item.title}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
            {selectedImage && (
                <div
                    onClick={closeImage}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0,0,0,0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999
                    }}
                >
                    <img
                        src={selectedImage}
                        alt="Preview"
                        style={{
                            maxWidth: "90%",
                            maxHeight: "90%",
                            borderRadius: "10px"
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default GalleryContent;